/**
 * Push-to-Talk Button Component
 *
 * Large touch-friendly button for recording voice messages.
 */

import { useCallback, useEffect, useRef } from 'react'
import { clsx } from 'clsx'

interface PTTButtonProps {
  isRecording: boolean
  isDisabled?: boolean
  queuePosition?: number
  recordingDuration: number
  maxDuration?: number
  onStart: () => void
  onStop: () => void
  className?: string
}

const MAX_DURATION_DEFAULT = 30 * 1000 // 30 seconds

export function PTTButton({
  isRecording,
  isDisabled = false,
  queuePosition: _queuePosition,
  recordingDuration,
  maxDuration = MAX_DURATION_DEFAULT,
  onStart,
  onStop,
  className,
}: PTTButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isPressed = useRef(false)

  // Format duration as mm:ss
  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Calculate remaining time - unused but may be used in future
  const remainingMs = maxDuration - recordingDuration
  void remainingMs // Suppress unused warning

  // Handle pointer events for both mouse and touch
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (isDisabled) return
      e.preventDefault()
      isPressed.current = true
      // Tactical Haptic Feedback
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(50) // Short buzz on press
      }
      onStart()
    },
    [isDisabled, onStart]
  )

  const handlePointerUp = useCallback(() => {
    if (isPressed.current) {
      isPressed.current = false
      if (isRecording) {
        // End haptic
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate([30, 50, 30]) // Double buzz confirmation
        }
        onStop()
      }
    }
  }, [isRecording, onStop])

  const handlePointerLeave = useCallback(() => {
    if (isPressed.current) {
      isPressed.current = false
      if (isRecording) {
        onStop()
      }
    }
  }, [isRecording, onStop])

  // Add global event listeners for pointer up (in case user releases outside button)
  useEffect(() => {
    const handleGlobalPointerUp = () => {
      if (isPressed.current) {
        isPressed.current = false
        if (isRecording) {
          onStop()
        }
      }
    }

    window.addEventListener('pointerup', handleGlobalPointerUp)
    window.addEventListener('pointercancel', handleGlobalPointerUp)

    return () => {
      window.removeEventListener('pointerup', handleGlobalPointerUp)
      window.removeEventListener('pointercancel', handleGlobalPointerUp)
    }
  }, [isRecording, onStop])

  // Prevent context menu on long press
  useEffect(() => {
    const button = buttonRef.current
    if (!button) return

    const handleContextMenu = (e: Event) => {
      e.preventDefault()
    }

    button.addEventListener('contextmenu', handleContextMenu)
    return () => button.removeEventListener('contextmenu', handleContextMenu)
  }, [])

  return (
    <div className={clsx('relative flex items-center justify-center gap-6 py-4', className)}>
      {/* Container for Button + Rings */}
      <div className="relative flex items-center justify-center">
        {/* Animated Rings - Centered behind button */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
          <div className={clsx(
            "w-48 h-48 border border-white/10 rounded-3xl border-dashed transition-all duration-500",
            isRecording ? "animate-spin-slow border-primary/30 scale-110" : "rotate-45"
          )}></div>
          <div className={clsx(
            "absolute w-40 h-40 border border-white/10 rounded-full border-dotted transition-all duration-500",
            isRecording ? "animate-spin-reverse border-primary/40" : ""
          )}></div>
        </div>

        {/* Main Button */}
        <div className="relative flex items-center justify-center active:scale-95 transition-transform duration-75">
          {/* Ping animation when recording */}
          {isRecording && (
            <div className="absolute w-32 h-32 rounded-2xl border-2 border-primary/40 animate-ping-slow mix-blend-screen"></div>
          )}

          <button
            ref={buttonRef}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            disabled={isDisabled}
            className={clsx(
              'w-28 h-28 flex flex-col items-center justify-center z-10 group cursor-pointer overflow-hidden transform hover:scale-[1.02] transition-all shadow-[0_4px_20px_rgba(0,0,0,0.8)]',
              // Shape & Materials
              'rounded-2xl border-[3px]',
              // Tactical "Knurled" Texture via CSS gradients
              'bg-[#1a1a1a]',
              'bg-[radial-gradient(circle_at_center,_transparent_0%,_#000_100%),repeating-linear-gradient(45deg,_rgba(255,255,255,0.03)_0px,_rgba(255,255,255,0.03)_1px,_transparent_1px,_transparent_4px),repeating-linear-gradient(-45deg,_rgba(255,255,255,0.03)_0px,_rgba(255,255,255,0.03)_1px,_transparent_1px,_transparent_4px)]',

              isRecording
                ? 'border-primary shadow-[0_0_30px_rgba(242,13,128,0.4)]'
                : 'border-white/10 hover:border-white/30',
              isDisabled && 'opacity-50 grayscale cursor-not-allowed'
            )}
            style={{
              // Deep inset shadow for physical feel
              boxShadow: isRecording
                ? 'inset 0 2px 10px rgba(0,0,0,0.8), 0 0 20px rgba(242,13,128,0.2)'
                : 'inset 0 2px 10px rgba(0,0,0,0.8), 0 10px 20px rgba(0,0,0,0.5)'
            }}
          >
            {/* Inner "LED" indicator */}
            <div className={clsx(
              "absolute top-3 w-8 h-1 rounded-full transition-all duration-200 shadow-inner",
              isRecording ? "bg-primary shadow-[0_0_10px_theme('colors.primary')]" : "bg-white/10"
            )}></div>

            <span className={clsx(
              "material-icons-round text-5xl transition-all duration-100 relative z-10 mt-2",
              isRecording ? "text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" : "text-white/20 group-hover:text-white/40 group-active:text-white/60"
            )}>
              mic
            </span>

            <span className={clsx(
              "font-mono text-[9px] font-bold tracking-[0.2em] leading-none mt-3 transition-colors relative z-10 uppercase",
              isRecording ? "text-primary animate-pulse" : "text-white/30"
            )}>
              {isRecording ? "TX ON" : "Hold"}
            </span>

            {/* Inner ring decoration */}
            <div className={clsx(
              "absolute inset-1 rounded-xl border border-dashed pointer-events-none transition-colors opacity-50",
              isRecording ? "border-primary/50" : "border-white/5"
            )}></div>
          </button>
        </div>
      </div>

      {/* Side Display: Timer & Status */}
      <div className="flex flex-col justify-center w-24 space-y-2 pointer-events-none">
        <div className="flex flex-col">
          <span className="text-[9px] text-white/40 uppercase tracking-widest font-mono mb-1">Status</span>
          <div className={clsx(
            "h-6 px-2 rounded-sm border flex items-center font-mono text-xs font-bold tracking-wider transition-all",
            isRecording
              ? "border-primary/50 bg-primary/10 text-primary shadow-[0_0_10px_rgba(242,13,128,0.2)]"
              : "border-white/10 bg-black/40 text-white/30"
          )}>
            {isRecording ? "TRANSMITTING" : "STANDBY"}
          </div>
        </div>

        <div className="flex flex-col">
          <span className="text-[9px] text-white/40 uppercase tracking-widest font-mono mb-1">Timer</span>
          <div className={clsx(
            "text-2xl font-display font-bold tracking-wider transition-all tabular-nums",
            isRecording ? "text-white neon-glow" : "text-white/20"
          )}>
            {isRecording ? formatDuration(recordingDuration) : "00:00"}
          </div>
        </div>

        {/* Signal Visualizer (Mock) */}
        <div className="flex items-end gap-0.5 h-6 opacity-80">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className={clsx(
              "flex-1 bg-white/10 rounded-sm transition-all duration-75",
              isRecording
                ? `bg-primary/80 animate-pulse`
                : "bg-white/5"
            )} style={{
              height: isRecording ? `${Math.random() * 100}%` : '20%',
              animationDelay: `${i * 50}ms`
            }}></div>
          ))}
        </div>
      </div>
    </div>
  )
}
